import {
    Feature,
    createActionFactoryForFeature,
    createThunk,
    createDataActionFactory,
    ThunkDispatch,
} from '../../../shared';
import {
    SearchState,
    getPackageState,
    getAppState,
    debouncedSerializeAndPersistPeopleHubOptions,
} from '../../../appstate';
import type { Dispatch } from 'redux';
import { getSearchResults, getFederatedSearchResults } from '../data/searchResultActions';
import { getSearchSuggestions } from '../data/searchSuggestionsActions';
import type { QueryData } from '../../models/query';
import debounce from 'lodash-es/debounce';
import { getQueryData } from '../../selectors/searchBoxSelectors';
import { SearchBoxState, packageStateKey } from '../../state';

const createActionFactory = createActionFactoryForFeature(Feature.Search);
export const searchSuggestedPersonaClickedAction = createActionFactory<{ hubPersonaId: string }>(
    'SearchSuggestedPersonaClickedAction'
);
export const searchResultResetAction = createActionFactory<{
    searchQuery: string;
    folderId: string;
}>('SearchResultResetAction');
export const searchModeExitClickAction = createActionFactory('SearchModeExitClickAction');
export const searchBoxInputClickedAction = createActionFactory('SearchBoxInputClickedAction');
export const searchAllQueriesExecutedAction = createDataActionFactory<
    { currentQuery: string },
    Error
>('SearchAllQueriesExecutedAction', Feature.Search);
export const searchFilterFolderOptionClickedAction = createActionFactory<{
    folderId: string;
    currentQuery: string;
}>('SearchFilterFolderOptionClickedAction');
export const searchBoxTextChangedAction = createActionFactory<{ newSearchText: string }>(
    'SearchBoxQueryChangedAction'
);
export const searchBoxPersonaPillRemovedAction = createActionFactory<{}>(
    'SearchBoxPersonaPillRemovedAction'
);

const debouncedGetSuggestions = debounce(
    (dispatch, queryData) => dispatch(getSearchSuggestions)(queryData),
    100
);

export const searchSuggestionClicked = (dispatch: Dispatch) => (hubPersonaId: string) =>
    dispatch(searchSuggestedPersonaClickedAction({ hubPersonaId }));

export const searchBoxEnterClicked = createThunk(
    'SearchBoxEnterClicked',
    (dispatch: ThunkDispatch, getState: () => {}) => (
        queryData: QueryData,
        searchState: SearchState
    ) => {
        const appState = getAppState(getState());
        dispatch(
            searchResultResetAction({
                searchQuery: queryData.currentQueryString,
                folderId: queryData.folderId,
            })
        );
        if (queryData.isAllFolder) {
            getFederatedSearchResults(dispatch)(queryData);
        } else {
            getSearchResults(dispatch)(queryData, appState.data);
        }
        debouncedSerializeAndPersistPeopleHubOptions(dispatch);
    }
);

export const searchRefresh = createThunk(
    'SearchRefresh',
    (dispatch: ThunkDispatch, getState: () => {}): void => {
        const queryData = getQueryData(
            getPackageState<SearchBoxState>(getState(), packageStateKey)
        );
        if (!queryData.currentQueryString) {
            return;
        }

        dispatch(
            searchResultResetAction({
                searchQuery: queryData.currentQueryString,
                folderId: queryData.folderId,
            })
        );
        getSearchResults(dispatch)(queryData, getAppState(getState()).data);
    }
);

export const searchBoxQueryChanged = (dispatch: Dispatch) => (queryData: QueryData) => {
    dispatch(searchBoxTextChangedAction({ newSearchText: queryData.currentQueryString }));
    debouncedGetSuggestions(dispatch, queryData);
};

export const searchModeExitClicked = (dispatch: Dispatch) => () =>
    dispatch(searchModeExitClickAction());

export const searchBoxInputClicked = (dispatch: Dispatch) => () =>
    dispatch(searchBoxInputClickedAction());

export const folderOptionClicked = (dispatch: ThunkDispatch) => (
    folderId: string,
    queryData: QueryData
) => {
    dispatch(
        searchFilterFolderOptionClickedAction({
            folderId: folderId,
            currentQuery: queryData.currentQueryString,
        })
    );
    /* Only dispatch searchRefresh if query text is present */
    if (queryData.currentQueryString) {
        dispatch(searchRefresh);
    }
};

export const personaPillRemoved = (dispatch: Dispatch) => () =>
    dispatch(searchBoxPersonaPillRemovedAction());
export type { Action } from '../../../shared';
export type { DataActionFactory } from '../../../shared';
